import type { MaybeRefOrGetter, Ref } from 'vue';
import { watch } from 'vue';

import { useLanguagePreferenceLoginWatcher } from './useLanguagePreferenceLoginWatcher';
import type { UserLanguagePreference } from './useLanguagePreferenceSync';

type AuthenticatedUser = UserLanguagePreference & { id: string };

/**
 * Wires login-time language sync and keeps Vuetify's locale aligned with vue-i18n.
 * Used by Helios and Luna `App.vue` setup.
 */
export function useAppAuthenticatedLocaleSync(options: {
  authenticatedUser: MaybeRefOrGetter<AuthenticatedUser | null | undefined>;
  locale: Ref<string>;
  vuetifyLocale: Ref<string>;
  getBaseLanguageCodeFromLocale: (locale: string) => string;
  onLocaleChange?: (locale: string) => void;
}) {
  const {
    authenticatedUser,
    locale,
    vuetifyLocale,
    getBaseLanguageCodeFromLocale,
    onLocaleChange
  } = options;

  function syncVuetifyLocaleFromI18n() {
    vuetifyLocale.value = getBaseLanguageCodeFromLocale(locale.value);
  }

  useLanguagePreferenceLoginWatcher(authenticatedUser, {
    onApplied: syncVuetifyLocaleFromI18n
  });

  watch(
    locale,
    newLocale => {
      syncVuetifyLocaleFromI18n();
      onLocaleChange?.(newLocale);
    },
    { immediate: true }
  );
}
